import Controller from "sap/ui/core/mvc/Controller";
import UIComponent from "sap/ui/core/UIComponent";
import AppComponent from "../Component";
import Model from "sap/ui/model/Model";
import ResourceModel from "sap/ui/model/resource/ResourceModel";
import ResourceBundle from "sap/base/i18n/ResourceBundle";
import Router from "sap/ui/core/routing/Router";
import History from "sap/ui/core/routing/History";
import MessageToast from "sap/m/MessageToast";
import MessageBox from "sap/m/MessageBox";
import Log from "sap/base/Log";
import "reflect-metadata";
import Input from "sap/m/Input";
import Popover from "sap/m/Popover";
import { User } from "../user";
import { clearAuth } from "../model/authStore";
import { Select$ChangeEvent } from "sap/m/Select";

/**
 * @namespace ang.order_portal.controller
 */
export default abstract class BaseController extends Controller {
	public popover: Popover;
	private modelInitializationPromise: Promise<void> | null = null;

	/**
	 * Convenience method for accessing the component of the controller's view.
	 * @returns The component of the controller's view
	 */
	public getOwnerComponent(): AppComponent {
		return super.getOwnerComponent() as AppComponent;
	}
	protected async getText(sKey: string, aArgs?: any[]): Promise<string> {
		return (await this.getResourceBundle()).getText(sKey, aArgs);
	}

	/**
	 * Convenience method to get the components' router instance.
	 * @returns The router instance
	 */
	public getRouter(): Router {
		return UIComponent.getRouterFor(this);
	}

	/**
	 * Convenience method for getting the i18n resource bundle of the component.
	 * @returns {Promise<sap.base.i18n.ResourceBundle>} The i18n resource bundle of the component
	 */
	public getResourceBundle(): Promise<ResourceBundle> {
		const oModel = this.getOwnerComponent().getModel("i18n") as ResourceModel;
		return oModel.getResourceBundle() as Promise<ResourceBundle>;
	}

	/**
	 * Convenience method for getting the view model by name in every controller of the application.
	 * @param [sName] The model name
	 * @returns The model instance
	 */
	public getModel(sName?: string): Model {
		return this.getView().getModel(sName);
	}

	/**
	 * Convenience method for setting the view model in every controller of the application.
	 * @param oModel The model instance
	 * @param [sName] The model name
	 * @returns The current base controller instance
	 */
	public setModel(oModel: Model, sName?: string): BaseController {
		this.getView().setModel(oModel, sName);
		return this;
	}

	/**
	 * Convenience method for triggering the navigation to a specific target.
	 * @public
	 * @param sName Target name
	 * @param [oParameters] Navigation parameters
	 * @param [bReplace] Defines if the hash should be replaced (no browser history entry) or set (browser history entry)
	 */
	public navTo(sName: string, oParameters?: object, bReplace?: boolean): void {
		this.getRouter().navTo(sName, oParameters, undefined, bReplace);
	}

	/**
	 * Convenience event handler for navigating back.
	 * It there is a history entry we go one step back in the browser history
	 * If not, it will replace the current entry of the browser history with the main route.
	 */
	public onNavBack(): void {
		const sPreviousHash = History.getInstance().getPreviousHash();
		if (sPreviousHash !== undefined) {
			window.history.go(-1);
		} else {
			this.getRouter().navTo("landingPage", {}, undefined, true);
		}
	}

	/**
	 * Shows a success message to the user
	 * @param message The message to display
	 * @param duration Optional duration in milliseconds (default: 3000)
	 */
	public showMessage(message: string, duration: number = 3000): void {
		MessageToast.show(message, { duration });
	}

	/**
	 * Shows a success message with a green checkmark icon
	 * @param message The success message to display
	 */
	public showSuccessMessage(message: string): void {
		MessageToast.show(message, {
			duration: 3000,
			width: "15em",
		});
		Log.info("Success: " + message, undefined, "BaseController");
	}

	/**
	 * Shows an error message with appropriate styling and logging
	 * @param message The error message to display
	 * @param error Optional error object for detailed logging
	 */
	public showErrorMessage(message: string, error?: any): void {
		// Show user-friendly message
		MessageToast.show(message, {
			duration: 5000,
			width: "20em",
		});

		// Log detailed error information
		if (error) {
			Log.error("Error: " + message, error, "BaseController");
			console.error("Detailed error:", error);
		} else {
			Log.error("Error: " + message, undefined, "BaseController");
		}
	}

	/**
	 * Shows a warning message to the user
	 * @param message The warning message to display
	 */
	public showWarningMessage(message: string): void {
		MessageToast.show(message, {
			duration: 4000,
			width: "18em",
		});
		Log.warning("Warning: " + message, undefined, "BaseController");
	}

	/**
	 * Shows a confirmation dialog with Yes/No buttons
	 * @param title The dialog title
	 * @param message The confirmation message
	 * @param onConfirm Callback function when user confirms
	 * @param onCancel Optional callback function when user cancels
	 */
	public showConfirmationDialog(
		title: string,
		message: string,
		onConfirm: () => void,
		onCancel?: () => void,
	): void {
		MessageBox.confirm(message, {
			title: title,
			onClose: (action: string) => {
				if (action === MessageBox.Action.OK) {
					onConfirm();
				} else if (onCancel) {
					onCancel();
				}
			},
		});
	}

	/**
	 * Shows an error dialog for critical errors
	 * @param title The dialog title
	 * @param message The error message
	 * @param error Optional error object for detailed logging
	 */
	public showErrorDialog(title: string, message: string, error?: any): void {
		if (error) {
			Log.error("Critical error: " + message, error, "BaseController");
			console.error("Critical error details:", error);
		}

		MessageBox.error(message, {
			title: title,
			details: error ? this.formatErrorDetails(error) : undefined,
		});
	}

	/**
	 * Shows an information dialog
	 * @param title The dialog title
	 * @param message The information message
	 */
	public showInfoDialog(title: string, message: string): void {
		MessageBox.information(message, { title: title });
	}

	/**
	 * Extracts and formats user-friendly error message from various error types
	 * @param error The error object
	 * @returns Formatted error message
	 */
	public extractErrorMessage(error: any): string {
		if (typeof error === "string") {
			return error;
		}

		if (typeof error === "object" && error !== null) {
			// Handle OpenAPI generated client errors
			if (error.body) {
				if (error.body.error?.message) {
					return Array.isArray(error.body.error.message)
						? error.body.error.message.join(", ")
						: error.body.error.message;
				}
				if (error.body.message) {
					return Array.isArray(error.body.message)
						? error.body.message.join(", ")
						: error.body.message;
				}
			}

			// Handle standard error objects
			if (error.message) {
				return error.message;
			}

			// Handle network/fetch errors
			if (error.name === "TypeError" && error.message?.includes("fetch")) {
				return "Network connection failed. Please check your internet connection.";
			}

			// Handle specific HTTP status codes
			if (error.status) {
				switch (error.status) {
					case 400:
						return "Invalid request. Please check your input.";
					case 401:
						return "Authentication failed. Please log in again.";
					case 403:
						return "Access denied. You don't have permission for this action.";
					case 404:
						return "Resource not found.";
					case 409:
						return "Conflict occurred. The resource may have been modified by another user.";
					case 422:
						return "Validation failed. Please check your input data.";
					case 500:
						return "Server error occurred. Please try again later.";
					case 503:
						return "Service temporarily unavailable. Please try again later.";
					default:
						return `Server returned error code ${error.status}`;
				}
			}
		}

		return "An unexpected error occurred. Please try again.";
	}

	/**
	 * Formats error details for display in error dialogs
	 * @param error The error object
	 * @returns Formatted error details string
	 */
	private formatErrorDetails(error: any): string {
		const details: string[] = [];

		if (error.status) {
			details.push(`Status: ${error.status}`);
		}

		if (error.code) {
			details.push(`Code: ${error.code}`);
		}

		if (error.timestamp) {
			details.push(`Time: ${new Date(error.timestamp).toLocaleString()}`);
		}

		if (error.stack && window.location.hostname === "localhost") {
			details.push(`Stack: ${error.stack}`);
		}

		return details.join("\n");
	}

	/**
	 * Handles async operations with loading indication and error handling
	 * @param operation The async operation to execute
	 * @param loadingMessage Optional loading message
	 * @param successMessage Optional success message
	 * @param errorMessage Optional error message prefix
	 */
	public async executeWithErrorHandling<T>(
		operation: () => Promise<T>,
		loadingMessage?: string,
		successMessage?: string,
		errorMessage?: string,
	): Promise<T | null> {
		try {
			if (loadingMessage) {
				this.showMessage(loadingMessage);
			}

			const result = await operation();

			if (successMessage) {
				this.showSuccessMessage(successMessage);
			}

			return result;
		} catch (error) {
			const message = errorMessage
				? `${errorMessage}: ${this.extractErrorMessage(error)}`
				: this.extractErrorMessage(error);

			this.showErrorMessage(message, error);
			return null;
		}
	}

	/**
	 * Initializes controller models in a consistent way.
	 * Runs optional synchronous setup first, then awaits all async loaders in parallel.
	 */
	protected initializeModels(
		synchronousSetup?: () => void,
		asyncLoaders: Array<() => Promise<unknown>> = [],
	): Promise<void> {
		synchronousSetup?.();

		this.modelInitializationPromise = Promise.all(
			asyncLoaders.map((loader) => loader()),
		)
			.then((): void => {
				return;
			})
			.catch((error) => {
				console.error("Initial model loading failed:", error);
			});

		return this.modelInitializationPromise;
	}

	protected async waitForInitializedModels(): Promise<void> {
		await (this.modelInitializationPromise ?? Promise.resolve());
	}

	protected async ensureModelsReady(
		isReady: () => boolean,
		warningMessage: string,
	): Promise<boolean> {
		await this.waitForInitializedModels();
		if (!isReady()) {
			this.showWarningMessage(warningMessage);
			return false;
		}

		return true;
	}

	/**
	 * onTogglePasswordVisibility
	 */
	async onTogglePasswordVisibility(event: any): Promise<void> {
		//TODO better
		let oButton = event.getSource();

		const oHBox = oButton.getParent(); // assume HBox
		const oInput = oHBox
			.getItems()
			.find((item: any) => item instanceof Input) as Input;

		if (!oInput) {
			this.showMessage("Input not found");
			return;
		}

		const currentType = oInput.getProperty("type");

		const newType = currentType === "Password" ? "Text" : "Password";

		oInput.setProperty("type", newType);
		oButton.setIcon(
			newType === "Password" ? "sap-icon://hide" : "sap-icon://show",
		);
	}

	async onProfileDropdown(event: any): Promise<void> {
		const avatar = event.getSource();
		if (!this.popover) {
			this.popover ??= (await this.loadFragment({
				name: "ang.order_portal.fragments.landingPage.ProfileDropDown",
			})) as Popover;
		}

		if (this.popover.isOpen()) {
			this.popover.close();
		} else {
			this.popover.openBy(avatar);
		}
	}

	onSignOut(): void {
		// Clear both auth systems for consistency
		clearAuth(); // Clear the auth store used by guards
		sessionStorage.removeItem("jwt"); // This is also done by clearAuth(), but keeping for safety
		User.clearData(); // Clear the old User singleton
		this.getRouter().navTo("auth", {}, true);
	}

	public onLanguageChange(event: Select$ChangeEvent): void {
		const oItem = event.getParameter("selectedItem");
		const selectedLanguage = oItem?.getKey();

		if (!selectedLanguage) {
			return;
		}

		// store preference (optional but recommended)
		localStorage.setItem("ui5.language", selectedLanguage);

		// Apply via bootstrap parameter (supported & non-deprecated approach)
		const url = new URL(globalThis.location.href);
		url.searchParams.set("sap-language", selectedLanguage);

		// keep hash/route, just reload with new bootstrap param
		globalThis.location.href = url.toString();
	}
}
